import { mockFinanceMeta, mockSettlements, type SettlementRecord } from '@/mocks/finance';
import { systemSettings } from '@/mocks/settings';
import { round2 } from '@/lib/pricing';
import type {
  DeliveryFeeSource,
  FinanceSummary,
  OrderFinanceView,
  OutstandingComponent,
  OutstandingDues,
  SettlementRequest,
} from '@/types';
import { delay, shouldSimulateError } from './simulation';
import { getOrderViews } from './ordersService';

export type FinancePeriod = 'today' | 'week' | 'month';

const PERIOD_MAX_DAYS: Record<FinancePeriod, number> = { today: 0, week: 6, month: 29 };

/** One derived finance row (recognition date + final amount + finance extras). */
interface FinanceRecord {
  orderId: string;
  /** 0 = recognised today … the service builds the recognition date from it. */
  recognitionDaysAgo: number;
  /** Final value after the contents review was adopted. */
  finalTotal: number;
  deliveryFeeToLaundry: number;
  deliveryFeeSource: DeliveryFeeSource;
  customerDebt: number;
  settled: boolean;
}

function buildDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString();
}

/** Whole days between an ISO date and today (calendar based, 0 = today). */
function daysSince(iso: string): number {
  const then = new Date(iso);
  const now = new Date();
  const a = new Date(then.getFullYear(), then.getMonth(), then.getDate()).getTime();
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.max(0, Math.round((b - a) / 86400000));
}

/**
 * Single source of truth: every finance row is derived from the SAME completed
 * orders that the homepage and the orders lists read. Only the finance-only
 * extras come from the demo metadata, so no number can contradict the orders.
 */
function buildRecords(): FinanceRecord[] {
  return getOrderViews()
    .filter((order) => order.status === 'completed' && order.completedAt)
    .map((order) => {
      const meta = mockFinanceMeta[order.id] ?? {
        deliveryFeeToLaundry: 0,
        deliveryFeeSource: 'none' as const,
        customerDebt: 0,
        settled: false,
      };
      return {
        orderId: order.id,
        recognitionDaysAgo: daysSince(order.completedAt as string),
        finalTotal: order.total,
        deliveryFeeToLaundry: meta.deliveryFeeToLaundry,
        deliveryFeeSource: meta.deliveryFeeSource,
        customerDebt: meta.customerDebt,
        settled: meta.settled,
      };
    });
}

function commissionRate(): number {
  return systemSettings.finance.commissionRate;
}

function computeView(record: FinanceRecord): OrderFinanceView {
  const rate = commissionRate();
  const commissionAmount = round2(record.finalTotal * rate);
  const netPayable = round2(record.finalTotal - commissionAmount + record.deliveryFeeToLaundry);
  return {
    orderId: record.orderId,
    recognizedAt: buildDate(record.recognitionDaysAgo),
    finalTotal: round2(record.finalTotal),
    commissionRate: rate,
    commissionAmount,
    deliveryFeeToLaundry: round2(record.deliveryFeeToLaundry),
    deliveryFeeSource: record.deliveryFeeSource,
    netPayable,
    customerDebt: round2(record.customerDebt),
  };
}

function inPeriod(record: FinanceRecord, period: FinancePeriod): boolean {
  return record.recognitionDaysAgo <= PERIOD_MAX_DAYS[period];
}

export interface RevenueResult {
  summary: FinanceSummary;
  details: OrderFinanceView[];
}

/** Revenue summary + commission details for the selected period only. */
export async function fetchRevenue(period: FinancePeriod): Promise<RevenueResult> {
  await delay();
  if (shouldSimulateError('finance')) {
    throw new Error('failed-to-load-finance');
  }

  const details = buildRecords()
    .filter((record) => inPeriod(record, period))
    .map(computeView)
    .sort((a, b) => new Date(b.recognizedAt).getTime() - new Date(a.recognizedAt).getTime());

  const directRevenue = round2(details.reduce((sum, d) => sum + d.finalTotal, 0));
  const transferredDeliveryFees = round2(
    details.reduce((sum, d) => sum + d.deliveryFeeToLaundry, 0),
  );

  return {
    summary: {
      directRevenue,
      transferredDeliveryFees,
      totalRevenue: round2(directRevenue + transferredDeliveryFees),
      currency: systemSettings.currency,
    },
    details,
  };
}

/** Outstanding (unsettled) balance. Independent from the revenue period. */
function computeOutstanding(): OutstandingDues {
  const components: OutstandingComponent[] = buildRecords()
    .filter((record) => !record.settled)
    .map((record) => {
      const view = computeView(record);
      return {
        id: record.orderId,
        type: 'order' as const,
        label: record.orderId,
        amount: view.netPayable,
        orderId: record.orderId,
      };
    });

  const total = round2(components.reduce((sum, component) => sum + component.amount, 0));

  return {
    total,
    currency: systemSettings.currency,
    components,
    packagePending: true,
  };
}

export async function fetchOutstanding(): Promise<OutstandingDues> {
  await delay();
  if (shouldSimulateError('finance')) {
    throw new Error('failed-to-load-finance');
  }
  return computeOutstanding();
}

let settlementStore: SettlementRecord[] = mockSettlements.map((record) => ({ ...record }));

function toSettlementView(record: SettlementRecord): SettlementRequest {
  return {
    id: record.id,
    requestedAt: buildDate(record.requestedDaysAgo),
    processedAt: record.processedDaysAgo === null ? null : buildDate(record.processedDaysAgo),
    amount: round2(record.amount),
    status: record.status,
    rejectionReason: record.rejectionReason,
    transferReceiptUrl: record.transferReceiptUrl,
  };
}

export async function fetchSettlements(): Promise<SettlementRequest[]> {
  await delay();
  if (shouldSimulateError('settlements')) {
    throw new Error('failed-to-load-settlements');
  }
  return settlementStore
    .map(toSettlementView)
    .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
}

/** Read a single settlement for its dedicated detail page. */
export async function fetchSettlementById(id: string): Promise<SettlementRequest | null> {
  await delay();
  if (shouldSimulateError('settlements')) {
    throw new Error('failed-to-load-settlement');
  }
  const record = settlementStore.find((item) => item.id === id);
  return record ? toSettlementView(record) : null;
}

/**
 * Submit a settlement request to the administration for the approved outstanding
 * balance. The amount is system-controlled (never typed by the user) and the
 * balance is NOT reduced until the system confirms a transfer.
 */
export async function requestSettlement(): Promise<SettlementRequest> {
  await delay(800);
  if (shouldSimulateError('settlements')) {
    throw new Error('failed-to-request-settlement');
  }
  const outstanding = computeOutstanding();
  if (outstanding.total <= 0) {
    throw new Error('no-outstanding');
  }
  const record: SettlementRecord = {
    id: `STL-${Date.now().toString().slice(-5)}`,
    requestedDaysAgo: 0,
    processedDaysAgo: null,
    amount: outstanding.total,
    status: 'processing',
    rejectionReason: null,
    transferReceiptUrl: null,
  };
  settlementStore = [record, ...settlementStore];
  return toSettlementView(record);
}